import { decodeJwtPayload, getAccessToken } from 'utils/auth';
import { TMXPageData, getNewSessionId } from 'utils/threatmetrix';
import { getBackofficeUrl } from 'utils/urls';

/**
 * Fire-and-forget `/api/risk` for a LOGIN event after a native biometric login.
 *
 * Password logins are risk-assessed by the auth app (LoginApp.js posts
 * eventType "login" from handleSuccess), and because the web biometric flow
 * routes back through handleSuccess it is covered there too. A native biometric
 * login never reaches that page — it unlocks the keystore and exchanges the
 * token in-app — so without this, biometric sign-ins would be invisible to
 * fraud monitoring, and would grow into a blind spot as adoption rises.
 *
 * Deliberately NOT called on the deep-link path: that login went through the
 * auth page, which already posted the event, and firing again would double-count.
 *
 * Shape mirrors firePostRiskForDetailsChange (same endpoint, auth header, and
 * fire-and-forget contract) with the login event type and page index. The two
 * are kept as separate small modules rather than refactored into one, matching
 * how the codebase already organises risk callsites per event type.
 */
export function fireLoginRisk(sessionJwt: string): void {
  let playerGUID = '';
  try {
    playerGUID = decodeJwtPayload(sessionJwt)?.sub || '';
  } catch {
    return;
  }
  if (!playerGUID) return;

  void (async () => {
    try {
      const sessionId = await getNewSessionId(TMXPageData.Login, playerGUID);
      if (!sessionId) return;

      const jwt = getAccessToken() || sessionJwt;
      await fetch(`${getBackofficeUrl()}/api/risk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${jwt}`
        },
        body: JSON.stringify({
          sessionId,
          eventType: 'login',
          playerGuid: playerGUID,
          amount: 0,
          pageId: TMXPageData.Login
        })
      });
    } catch {
      // Fire-and-forget, same as the details-change callsite: a failed post has
      // no UI consequence and must never block the player getting into the app.
    }
  })();
}
